import React from 'react';
import { List, ListItem, ListItemIcon } from '@fiverr-private/list';

import { Stack } from '@fiverr-private/layout_components';
import { Image } from '@fiverr-private/media';
import { Heading, Link, Text, Typography } from '@fiverr-private/typography';
import { CheckIcon } from '@fiverr-private/visuals';
import { Button } from '@fiverr-private/button';
import pathfinder from '@fiverr-private/pathfinder';
import { DrawerBody, DrawerHeader } from '@fiverr-private/overlay';
import { mixpanel } from '@fiverr-private/obs';
import { AppI18n } from '../../i18n';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { briefCreationClickAction } from '../../monitoring';
import BriefCreationCloseButton from '../BriefCreationDrawerComponent/BriefCreationCloseButton';
import { AUCTION_SOURCE, MGMT_PAGE_TABS } from '../../constants';
import { migrateUserToPro, setMigrationAttemptDateToLocalStorage, MigrationType } from './utils';

const copyKeys: Record<
  MigrationType,
  'pro_migration_step.high_budget' | 'pro_migration_step.very_high_budget' | 'pro_migration_step.hourly'
> = {
  rewards_program: 'pro_migration_step.high_budget',
  high_budget: 'pro_migration_step.very_high_budget',
  hourly: 'pro_migration_step.hourly',
};

const BENEFIT_KEYS: Partial<Record<MigrationType, readonly string[]>> = {
  rewards_program: ['benefit_2', 'benefit_3', 'benefit_priority_support'],
  high_budget: ['benefit_1', 'benefit_2', 'benefit_priority_support'],
};

interface MigrationStepContentProps {
  migrationType: MigrationType;
}

const BenefitsListItem = ({ copyKey }: { copyKey: any }) => (
  <ListItem direction="row" display="flex" gap="4">
    <ListItemIcon>
      <CheckIcon size="md" color="grey_1200" />
    </ListItemIcon>
    <Text size="b_sm">
      <AppI18n k={copyKey} />
    </Text>
  </ListItem>
);

export const MigrationStepContent = ({ migrationType }: MigrationStepContentProps) => {
  const { brief, currentStep, contextId, source, setIsOpen, variant, parentElement, isAllocatedAuctionInviteFlow } =
    useBriefCreationContext();
  const copyKey = copyKeys[migrationType];
  const isOnboardingRouting = variant === 'onboarding_routing';

  const benefitKeys: readonly string[] = BENEFIT_KEYS[migrationType] ?? ['benefit_1', 'benefit_2', 'benefit_3'];

  const imgUrl: Record<MigrationType, string> = {
    rewards_program:
      'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/094be3767023de6d3543b6cde28fb1dd-1747211684828/Screenshot%202025-05-05%20at%2014.37.48%201.png',
    high_budget:
      'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/e3c430fd21956b37ecfe8ce734f98a04-1741354342505/very_high_budget.png',
    hourly:
      'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/e3c430fd21956b37ecfe8ce734f98a04-1741354342502/hourly_pay.png',
  };

  const onXClick = () => {
    mixpanel.track('Job post migration - exit - clicked', {});
  };

  const handleSubmit = () => {
    setIsOpen(false);
    mixpanel.track('Job post migration - upgrade to pro - clicked', {});

    briefCreationClickAction({
      step: currentStep,
      action: 'upgrade_to_pro',
      briefContextId: contextId,
      eventSource: source || '',
      parentElement,
      variant,
    });

    migrateUserToPro(brief?.id, migrationType, isAllocatedAuctionInviteFlow);
  };

  const onCancel = () => {
    setMigrationAttemptDateToLocalStorage();

    mixpanel.track('Job post migration - stay on fiverr - clicked', {});

    briefCreationClickAction({
      step: currentStep,
      action: 'stay_on_fiverr',
      briefContextId: contextId,
      eventSource: source || '',
      parentElement,
      variant,
    });
  };

  return (
    <>
      {!isOnboardingRouting && (
        <DrawerHeader>
          <BriefCreationCloseButton onClose={onXClick} />
        </DrawerHeader>
      )}
      <DrawerBody>
        <Stack width="100%" height="100%" direction="column" justifyContent="spaceBetween">
          <Stack gap="0" direction="column">
            <Image
              borderRadius="xl"
              alignSelf="center"
              src={imgUrl[migrationType]}
              alt="final brief"
              width="408px"
              height="204px"
            />
            <Heading marginTop="16" as="h4">
              <AppI18n k={`${copyKey}.title`} />
            </Heading>
            <List padding="0" marginTop="6">
              {benefitKeys.map((suffix) => (
                <BenefitsListItem key={suffix} copyKey={`${copyKey}.${suffix}`} />
              ))}
            </List>
            <Text marginTop="10" fontWeight="semibold">
              <AppI18n k={`${copyKey}.warning`} />
            </Text>
            <Button marginTop="4" onClick={handleSubmit}>
              <AppI18n k={`${copyKey}.submit_cta`} />
            </Button>
            <Button
              href={pathfinder(
                'dynamic_matching_management_page',
                { briefId: brief?.id || '', tab: isAllocatedAuctionInviteFlow ? MGMT_PAGE_TABS.INVITE : 'undefined' },
                { query: { source: isAllocatedAuctionInviteFlow ? AUCTION_SOURCE : 'dm_brief_creation' } }
              )}
              onClick={onCancel}
              marginTop="4"
              variant="ghost"
            >
              <AppI18n k={`${copyKey}.cancel_cta`} />
            </Button>
          </Stack>
          <Typography textAlign="center" color="grey_900" fontSize="b_xs" lineHeight="b_xs">
            <AppI18n
              k={`${copyKey}.tos`}
              templates={{
                link: (text) => (
                  <Link
                    color="grey_900"
                    href={pathfinder('business_terms_of_service')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {text}
                  </Link>
                ),
              }}
            />
          </Typography>
        </Stack>
      </DrawerBody>
    </>
  );
};
